import {
  Checkbox as HeadlessCheckbox,
  Label,
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from "@headlessui/react";
import { clsx } from "clsx";

import { Skeleton } from "shared/ui";

import type { PropsWithChildren } from "react";

interface ICheckboxProps {
  name: string;
  invalid?: boolean;
  onChange?: (checked: boolean) => void;
}

export function Checkbox({ name, invalid, children, onChange }: PropsWithChildren<ICheckboxProps>) {
  return (
    <div className="flex items-baseline gap-2">
      <HeadlessCheckbox
        className={`group block size-5 flex-center cursor-pointer rounded-rounded border bg-athens-gray data-[checked]:border-shark data-[checked]:bg-accent data-[checked]:ring-0 ${invalid ? "border-carnation ring-1 ring-carnation" : "border-shark"}`}
        name={name}
        defaultChecked={false}
        onChange={onChange}
      >
        <div className="text-bg_middle leading-none opacity-0 group-data-[checked]:opacity-100">
          &#10004;
        </div>
      </HeadlessCheckbox>
      <Label className="flex-1 text-12 text-white">{children}</Label>
    </div>
  );
}

export function Separator({ children }: PropsWithChildren) {
  return (
    <div className="my-5 flex items-center">
      <div className="flex-grow border-bg_middle border-t" />
      <span className="mx-3 flex-shrink">{children}</span>
      <div className="flex-grow border-bg_middle border-t" />
    </div>
  );
}

export function AuthEndpointsContainer({ children }: PropsWithChildren) {
  return (
    <div className="grid grid-cols-[repeat(auto-fit,8.125rem)] justify-center gap-3 *:h-10">
      {children}
    </div>
  );
}

export function AuthEndpointsSkeleton() {
  return (
    <>
      <Skeleton className="my-5 h-6" />
      <AuthEndpointsContainer>
        <Skeleton />
        <Skeleton />
        <Skeleton />
      </AuthEndpointsContainer>
    </>
  );
}

export function CurrencySelector({ currencies }: { currencies: string[] }) {
  return (
    <Listbox name="currency" defaultValue={currencies[0]}>
      <ListboxButton
        className={clsx(
          "flex items-center gap-2 rounded-rounded border-1 border-bg_middle bg-bg_dark px-3 py-1.5 font-bold",
          "data-[focus]:-outline-offset-2 focus:outline-none data-[focus]:outline-2 data-[focus]:outline-white/25",
        )}
      >
        {({ value }) => (
          <>
            {value}
            <svg width="7" height="7" fill="none" viewBox="0 0 7 7">
              <path
                className="fill-white"
                d="M4.366 6a1 1 0 0 1-1.732 0L.469 2.25a1 1 0 0 1 .866-1.5h4.33a1 1 0 0 1 .866 1.5L4.366 6Z"
              />
            </svg>
          </>
        )}
      </ListboxButton>
      <ListboxOptions
        anchor={{
          to: "bottom start",
          gap: "0.25rem",
        }}
        transition
        className="z-[902] origin-bottom rounded-rounded border-1 border-bg_middle bg-bg_dark p-2 font-bold transition duration-200 ease-out data-[closed]:scale-95 data-[closed]:opacity-0 lg:p-1.5"
      >
        {currencies.map((currency) => (
          <ListboxOption
            key={currency}
            value={currency}
            className="cursor-default rounded-rounded px-3 py-1 lg:px-3 lg:py-1.5 lg:data-[focus]:bg-accent lg:data-[focus]:text-bg_dark"
          >
            {currency}
          </ListboxOption>
        ))}
      </ListboxOptions>
    </Listbox>
  );
}
